import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowRight,
  Bug,
  Check,
  CheckSquare,
  Copy,
  Maximize2,
  Minimize2,
  Loader2,
  PenLine,
  RotateCcw,
  Search,
  Sparkles,
  X,
} from "lucide-react";
import { assistantService } from "../../api/assistantService";
import useAuth from "../../hooks/useAuth";
import { useMediaQuery } from "../../hooks/useMediaQuery";
import ABSMark from "../../assets/ABS-Logo/Vector/Vector.png";
import useAssistantChat from "./useAssistantChat";
import { activeDraft, firstNameOf, parseBlocks, parseInline, messageText } from "./assistantText";
import "./assistant.css";

const SEEN_KEY = "abs-assistant-seen";
const CONFIRM_QUESTION = "Do you want me to create this task?";

// Cards fill the composer instead of sending: a task needs the user's own details.
const SUGGESTIONS = [
  { label: "New feature task", icon: CheckSquare, text: "I need a new feature: " },
  { label: "Report a bug to fix", icon: Bug, text: "Fix this bug: " },
  { label: "Investigate an issue", icon: Search, text: "Investigate why " },
  { label: "Write it from Arabic notes", icon: PenLine, text: "حوّل الملاحظات دي لتاسك: " },
];
const FOLLOW_UPS = ["Make it shorter", "Add more acceptance criteria", "Change the type to fix"];

// Model name is the same for everyone: fetch it once per page load.
let modelPromise = null;
const loadModel = () => {
  if (!modelPromise) {
    modelPromise = assistantService.info().then((r) => r?.data?.model || null).catch(() => {
      modelPromise = null; // try again next time the chat opens
      return null;
    });
  }
  return modelPromise;
};

const readSeen = () => {
  try {
    return localStorage.getItem(SEEN_KEY) === "1";
  } catch {
    return false;
  }
};

const Inline = ({ text }) =>
  parseInline(text).map((p, i) =>
    p.type === "bold" ? <strong key={i}>{p.value}</strong> : p.type === "code" ? <code key={i} className="aia-code">{p.value}</code> : <React.Fragment key={i}>{p.value}</React.Fragment>,
  );

const Blocks = ({ text, caret }) => {
  const blocks = parseBlocks(text);
  if (!blocks.length) return caret ? <span className="aia-caret" /> : null;
  return blocks.map((b, i) => (
    <div key={i} className={`aia-line${b.bullet ? " aia-bullet" : ""}`} dir="auto">
      <span>
        <Inline text={b.text} />
        {caret && i === blocks.length - 1 && <span className="aia-caret" />}
      </span>
    </div>
  ));
};

const DraftCard = ({ msg, onConfirm, onOpen, isLatestPending }) => {
  const { draft, status } = msg;
  const isFeat = draft.taskType === "feat";
  return (
    <div className="aia-draft">
      <span className="aia-type">{draft.taskType}</span>
      <div className="aia-draft-title"><Inline text={draft.title} /></div>
      <div>
        <div className="aia-sec">{isFeat ? "User story" : "Description"}</div>
        <Blocks text={isFeat ? draft.userStory : draft.description} />
      </div>
      <div>
        <div className="aia-sec">Acceptance criteria</div>
        {draft.acceptanceCriteria.map((c, i) => (
          <div key={i} className="aia-line aia-bullet" dir="auto"><span><Inline text={c} /></span></div>
        ))}
      </div>

      {status === "created" ? (
        <div className="aia-ask-row">
          <div className="aia-created"><Check size={16} /> Task{msg.task?.number ? ` #${msg.task.number}` : ""} created in Product Backlog</div>
          <div className="aia-btns"><button type="button" className="aia-ghost" onClick={() => onOpen(msg.task)}>Open task <ArrowRight size={14} /></button></div>
        </div>
      ) : status === "superseded" ? (
        <div className="aia-ask-row"><span className="aia-note">Replaced by a newer draft.</span></div>
      ) : (
        <div className="aia-ask-row">
          <div>{CONFIRM_QUESTION}</div>
          {msg.error && <div className="aia-error" role="alert">{msg.error}</div>}
          {isLatestPending && (
            <>
              <div className="aia-btns">
                <button type="button" className="aia-create" aria-disabled={status === "creating"} onClick={() => status !== "creating" && onConfirm(msg.id)}>
                  {status === "creating" ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
                  {status === "creating" ? "Creating…" : "Yes, create task"}
                </button>
              </div>
              <span className="aia-note">Or tell me what to change and I'll update the draft.</span>
            </>
          )}
        </div>
      )}
    </div>
  );
};

const AssistantWidget = () => {
  const { auth } = useAuth();
  const navigate = useNavigate();
  const employee = auth?.employee;
  const userId = employee?._id ?? employee?.id;
  const name = firstNameOf(employee?.fullNameEn || employee?.fullNameAr || employee?.nickname);
  const chat = useAssistantChat(userId ? String(userId) : null);
  const { messages, typing, tw } = chat;

  const [open, setOpen] = useState(false);
  const [closing, setClosing] = useState(false);
  const [wasOpen, setWasOpen] = useState(false);
  const [seen, setSeen] = useState(readSeen);
  const [full, setFull] = useState(false);
  const [input, setInput] = useState("");
  const [copied, setCopied] = useState(null);
  const [model, setModel] = useState(null);
  const narrow = useMediaQuery("(max-width: 599px)");
  const isFull = full || narrow;

  const scrollRef = useRef(null);
  const inputRef = useRef(null);
  const closeTimer = useRef(null);
  const copyTimer = useRef(null);
  useEffect(() => () => { clearTimeout(closeTimer.current); clearTimeout(copyTimer.current); }, []);

  const openChat = () => {
    setOpen(true);
    setWasOpen(true);
    if (!seen) {
      setSeen(true);
      try { localStorage.setItem(SEEN_KEY, "1"); } catch { /* private mode: badge just shows again */ }
    }
  };
  const closeChat = useCallback(() => {
    setClosing(true);
    clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => { setOpen(false); setClosing(false); }, 260);
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => e.key === "Escape" && closeChat();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, closeChat]);

  useEffect(() => { if (open) inputRef.current?.focus(); }, [open]);
  useEffect(() => {
    if (!open || model) return undefined;
    let live = true;
    loadModel().then((m) => live && m && setModel(m));
    return () => { live = false; };
  }, [open, model]);

  // Grow the textarea up to 120px.
  useLayoutEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, 120)}px`;
  }, [input, open]);

  // Jump to the bottom on a new message; follow the typewriter only if already near the bottom.
  const count = messages.length + (typing ? 1 : 0);
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [count, open]);
  useEffect(() => {
    const el = scrollRef.current;
    if (el && tw && el.scrollHeight - el.scrollTop - el.clientHeight < 120) el.scrollTop = el.scrollHeight;
  }, [tw]);

  const submit = (text) => {
    if (!text.trim() || typing) return;
    setInput("");
    chat.send(text);
  };
  const copy = (idx, m) => {
    try { navigator.clipboard.writeText(messageText(m)); } catch { /* clipboard blocked */ }
    setCopied(idx);
    clearTimeout(copyTimer.current);
    copyTimer.current = setTimeout(() => setCopied(null), 1400);
  };
  const openTask = (task) => {
    if (!task?.id) return;
    navigate(`/task-board?task=${task.id}`);
    if (isFull) closeChat();
  };
  const prefill = (text) => {
    setInput(text);
    requestAnimationFrame(() => {
      const el = inputRef.current;
      if (el) { el.focus(); el.setSelectionRange(text.length, text.length); }
    });
  };

  if (!employee) return null;

  if (!open) {
    return (
      <div className="aia-root aia-launcher">
        {!seen && (
          <div className="aia-peek" onClick={openChat} role="button" tabIndex={0} onKeyDown={(e) => e.key === "Enter" && openChat()}>
            <div style={{ fontWeight: 600, marginBottom: 2 }}>Hey {name}</div>
            <div style={{ color: "var(--muted)" }}>Ask me anything, or tell me a requirement and I'll draft the task.</div>
          </div>
        )}
        <button type="button" className={`aia-fab${wasOpen ? " aia-fab-back" : ""}`} onClick={openChat} title="ABS Assistant" aria-label="Open ABS Assistant">
          <Sparkles size={26} fill="currentColor" />
          {!seen && <span className="aia-badge">1</span>}
        </button>
      </div>
    );
  }

  const latestPending = activeDraft(messages);
  const last = messages[messages.length - 1];
  const busy = typing || !!tw;
  const showFollow = !busy && last?.kind === "draft" && last.status === "pending" && messages.length <= 2;
  const empty = !messages.length && !typing;
  const hasText = !!input.trim();

  return (
    <div className="aia-root">
      <section
        className={`aia-panel${isFull ? " aia-full" : ""}${closing ? " aia-closing" : ""}`}
        role="dialog"
        aria-label="ABS Assistant"
      >
        <div className="aia-glow" />

        <header className="aia-head">
          <div className="aia-avatar">
            <div className="aia-avatar-ring" />
            <div className="aia-avatar-in"><img src={ABSMark} alt="ABS" style={{ height: 15, display: "block" }} /></div>
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="aia-title">ABS Assistant</div>
            <div className={`aia-status${busy ? " aia-busy" : ""}`}>
              <span className="aia-status-dot" />
              {typing ? "Thinking…" : tw ? "Typing…" : model ? `Online · powered by ${model}` : "Online"}
            </div>
          </div>
          <div style={{ display: "flex", gap: 2 }}>
            <button type="button" className="aia-iconbtn" onClick={chat.reset} title="New chat" aria-label="New chat"><RotateCcw size={16} /></button>
            {!narrow && (
              <button type="button" className="aia-iconbtn" onClick={() => setFull((f) => !f)} title={isFull ? "Collapse" : "Expand"} aria-label={isFull ? "Collapse" : "Expand"}>
                {isFull ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
              </button>
            )}
            <button type="button" className="aia-iconbtn" onClick={closeChat} title="Close" aria-label="Close"><X size={16} /></button>
          </div>
        </header>

        <div className="aia-body" ref={scrollRef}>
          <div className="aia-col" style={isFull && empty ? { paddingTop: "8vh" } : undefined}>
            {empty ? (
              <div className="aia-hero">
                <div className="aia-hello">Hi {name},</div>
                <div className="aia-ask">how can I help today?</div>
                <p className="aia-intro">Ask me anything, or describe a requirement in Arabic or English and I'll turn it into a clear task. I only create a task after you confirm.</p>
                <div className="aia-sugs">
                  {SUGGESTIONS.map(({ label, icon: Icon, text }) => (
                    <button key={label} type="button" className="aia-sug" onClick={() => prefill(text)}>
                      <span className="aia-sug-ic"><Icon size={15} /></span>
                      {label}
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <div className="aia-msgs" aria-live="polite">
                {messages.map((m, idx) => {
                  const bot = m.role === "assistant";
                  const typingThis = bot && tw?.id === m.id;
                  const isLast = idx === messages.length - 1;
                  const showActs = bot && !typingThis && !typing;
                  const canRetry = m.kind !== "draft" || m.status === "pending" || m.status === "superseded";
                  return (
                    <div key={m.id || idx} className="aia-msg">
                      <div className={`aia-row${bot ? "" : " aia-user"}`}>
                        {bot && <div className="aia-bot-av"><Sparkles size={13} fill="currentColor" /></div>}
                        {bot ? (
                          <div className={`aia-bubble aia-a${m.err ? " aia-err" : ""}`}>
                            {m.kind === "draft" ? (
                              <DraftCard msg={m} onConfirm={chat.confirm} onOpen={openTask} isLatestPending={latestPending?.id === m.id} />
                            ) : (
                              <Blocks text={typingThis ? tw.shown : m.content} caret={typingThis} />
                            )}
                          </div>
                        ) : (
                          <div className="aia-bubble aia-u" dir="auto">{m.content}</div>
                        )}
                      </div>
                      {showActs && (
                        <div className={`aia-acts${isLast || copied === idx ? "" : " aia-hover-only"}`}>
                          <button type="button" className={`aia-act${copied === idx ? " aia-ok" : ""}`} onClick={() => copy(idx, m)} title="Copy" aria-label="Copy">
                            {copied === idx ? <Check size={13} /> : <Copy size={13} />}
                            {copied === idx && "Copied"}
                          </button>
                          {canRetry && (
                            <button type="button" className="aia-act" onClick={() => chat.retry(idx)} title="Retry" aria-label="Retry">
                              <RotateCcw size={13} /> Retry
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
                {typing && (
                  <div className="aia-row">
                    <div className="aia-bot-av"><Sparkles size={13} fill="currentColor" /></div>
                    <div className="aia-bubble aia-a"><div className="aia-dots" aria-label="Thinking"><span /><span /><span /></div></div>
                  </div>
                )}
                {showFollow && (
                  <div className="aia-chips">
                    {FOLLOW_UPS.map((f) => (
                      <button key={f} type="button" className="aia-chip" onClick={() => submit(f)}>{f}</button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        <footer className="aia-foot">
          <div className="aia-col">
            <div className="aia-composer">
              <textarea
                ref={inputRef}
                rows={1}
                dir="auto"
                className="aia-input"
                value={input}
                placeholder="Ask anything…"
                aria-label="Message"
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) {
                    e.preventDefault();
                    submit(input);
                  }
                }}
              />
              <button type="button" className={`aia-send${hasText && !typing ? " aia-on" : ""}`} disabled={!hasText || typing} onClick={() => submit(input)} aria-label="Send">
                <ArrowRight size={18} />
              </button>
            </div>
            <div className="aia-disclaimer">Answers can be wrong. Double-check anything important.</div>
          </div>
        </footer>
      </section>
    </div>
  );
};

export default AssistantWidget;
